import { downloadFile } from "@/utils/download-file";
import { FileDownload, useFileManager } from "@jace/file-uploader";
import {
	Button,
	ButtonGroup,
	Content,
	Icon,
	Modal,
	Tooltip,
	VStack,
	cn,
	useKeyboardShortcut,
} from "@jace/ui";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFilePreviewStore } from "./file-previewer";
import {
	DocxRenderer,
	ImageRenderer,
	PdfRenderer,
	SpreadsheetRenderer,
	TextRenderer,
} from "./renderers";

function blockEvent(event: React.MouseEvent<HTMLButtonElement>) {
	event.preventDefault();
	event.stopPropagation();
}

function formatFileName(
	name: string | undefined | null,
	type: string | undefined | null,
): string {
	if (!name) return "";
	const cleanName = String(name);
	const cleanType = type?.toLowerCase() ?? "";
	if (cleanName.toLowerCase().endsWith(`.${cleanType}`)) {
		return cleanName;
	}
	return cleanType ? `${cleanName}.${cleanType}` : cleanName;
}

function ZoomControls({
	zoom,
	onZoomIn,
	onZoomOut,
	onResetZoom,
	showPageNumber,
	currentPage,
	totalPages,
	pageNumberLabel,
	onPageChange,
}: {
	zoom: number;
	onZoomIn: () => void;
	onZoomOut: () => void;
	onResetZoom: () => void;
	pageNumberLabel?: string;
	showPageNumber?: boolean;
	currentPage?: number;
	totalPages?: number;
	onPageChange?: (page: number) => void;
}): React.ReactElement {
	const [inputValue, setInputValue] = useState<string>("");
	const [isEditing, setIsEditing] = useState(false);
	const [isVisible, setIsVisible] = useState(true);
	const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
	const isScrollingRef = useRef(false);
	const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
	const isHoveringRef = useRef(false);

	useEffect(() => {
		const startHideTimer = (): void => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			if (!isScrollingRef.current && !isHoveringRef.current) {
				timeoutRef.current = setTimeout(() => {
					setIsVisible(false);
				}, 2000);
			}
		};

		const handleMouseMove = (): void => {
			setIsVisible(true);
			startHideTimer();
		};

		const handleScroll = (): void => {
			setIsVisible(true);
			isScrollingRef.current = true;

			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current);
			}

			scrollTimeoutRef.current = setTimeout(() => {
				isScrollingRef.current = false;
				startHideTimer();
			}, 150); // Wait a bit after scroll stops before starting hide timer
		};

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("scroll", handleScroll, true);

		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("scroll", handleScroll, true);
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current);
			}
		};
	}, []);

	const handleMouseEnter = (): void => {
		isHoveringRef.current = true;
		setIsVisible(true);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
	};

	const handleMouseLeave = (): void => {
		isHoveringRef.current = false;
		if (!isScrollingRef.current) {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = setTimeout(() => {
				setIsVisible(false);
			}, 2000);
		}
	};

	const handlePageClick = () => {
		if (!isEditing) {
			setIsEditing(true);
			setInputValue(currentPage?.toString() || "");
		}
	};

	const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.replace(/[^0-9]/g, "");
		setInputValue(value);
	};

	const handlePageInputBlur = () => {
		setIsEditing(false);
		if (!inputValue) return;

		const newPage = Number.parseInt(inputValue, 10);
		if (
			newPage &&
			newPage >= 1 &&
			newPage <= (totalPages || 0) &&
			onPageChange
		) {
			onPageChange(newPage);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault();
			if (!inputValue) return;

			const newPage = Number.parseInt(inputValue, 10);
			if (
				newPage &&
				newPage >= 1 &&
				newPage <= (totalPages || 0) &&
				onPageChange
			) {
				onPageChange(newPage);
			}
			setIsEditing(false);
		} else if (e.key === "Escape") {
			setIsEditing(false);
		}
	};

	const pageNumberWidth = totalPages
		? Math.max(totalPages.toString().length * 12, 24)
		: 24;

	return (
		<div
			className={cn(
				"absolute right-4 bottom-4 z-50 flex items-center gap-2 rounded-lg bg-surface-inverted p-2 shadow-sm transition-opacity duration-300",
				isVisible ? "opacity-100" : "opacity-0",
			)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{showPageNumber && currentPage !== undefined && (
				<>
					<div className="flex items-center gap-2 px-2 text-sm text-text-body">
						<span
							className={
								pageNumberLabel && pageNumberLabel.length > 0 ? "" : "hidden"
							}
						>
							{pageNumberLabel !== undefined ? pageNumberLabel : "Page"}
						</span>
						{isEditing ? (
							<input
								type="text"
								value={inputValue}
								onChange={handlePageInputChange}
								onBlur={handlePageInputBlur}
								onKeyDown={handleKeyDown}
								style={{ width: `${pageNumberWidth}px` }}
								className="rounded bg-surface px-1 text-center"
							/>
						) : (
							<button
								type="button"
								onClick={handlePageClick}
								style={{ width: `${pageNumberWidth}px` }}
								className="cursor-text text-center hover:text-text-body-muted"
							>
								{currentPage}
							</button>
						)}
						<span>
							/{" "}
							<span
								style={{
									display: "inline-block",
									width: `${pageNumberWidth}px`,
									textAlign: "center",
								}}
							>
								{totalPages}
							</span>
						</span>
					</div>
					<div className="h-4 w-[1px] bg-border" />
				</>
			)}
			<ButtonGroup>
				<Button
					className="-mr-1 text-text-icon outline-none hover:text-text-icon/80"
					size="icon"
					intent="ghost"
					onClick={onZoomOut}
				>
					<Icon kind="zoom-out" />
				</Button>
				<button
					className="min-w-[48px] cursor-pointer text-center text-sm text-text-body-muted hover:text-text-body"
					onClick={onResetZoom}
					type="button"
				>
					{Math.round(zoom * 100)}%
				</button>
				<Button
					className="-ml-1 text-text-icon outline-none hover:text-text-icon/80"
					size="icon"
					intent="ghost"
					onClick={onZoomIn}
				>
					<Icon kind="zoom-in" />
				</Button>
			</ButtonGroup>
		</div>
	);
}

interface NavigationButtonsProps {
	currentIndex: number;
	attachmentsLength: number;
	onPrevious: (event: React.MouseEvent<HTMLButtonElement>) => void;
	onNext: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function NavigationButtons({
	currentIndex,
	attachmentsLength,
	onPrevious,
	onNext,
}: NavigationButtonsProps): React.ReactElement {
	return (
		<div
			aria-hidden="true"
			className="pointer-events-none fixed inset-0 hidden"
			style={{ zIndex: 99999 }}
		>
			<div className="flex h-full items-center justify-between px-12">
				<div className="pointer-events-auto">
					<Tooltip text="Previous file">
						<Button
							className="isolate h-10 w-10 rounded-full bg-surface/50 p-0 text-text-body backdrop-blur-sm hover:bg-surface/70 hover:text-text-body-muted disabled:pointer-events-none"
							disabled={currentIndex === 0}
							intent="ghost"
							onClick={onPrevious}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									onPrevious(
										e as unknown as React.MouseEvent<HTMLButtonElement>,
									);
								}
							}}
							size="sm"
						>
							<Icon className="h-5 w-5" kind="arrow-left" />
						</Button>
					</Tooltip>
				</div>
				<div className="pointer-events-auto">
					<Tooltip text="Next file">
						<Button
							className="isolate h-10 w-10 rounded-full bg-surface/50 p-0 text-text-body backdrop-blur-sm hover:bg-surface/70 hover:text-text-body-muted disabled:pointer-events-none"
							disabled={currentIndex === attachmentsLength - 1}
							intent="ghost"
							onClick={onNext}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									onNext(e as unknown as React.MouseEvent<HTMLButtonElement>);
								}
							}}
							size="sm"
						>
							<Icon className="h-5 w-5" kind="arrow-right" />
						</Button>
					</Tooltip>
				</div>
			</div>
		</div>
	);
}

export function FilePreviewModal(): React.ReactElement {
	const manager = useFileManager();
	const [file, setFile] = useState<FileDownload | null>(null);
	const { isOpen, close, next, prev, data, current, total } =
		useFilePreviewStore();

	const [zoom, setZoom] = useState(0.8);
	const portalContainerRef = useRef<HTMLDivElement | null>(null);

	useKeyboardShortcut("Escape", close, !isOpen, 2);
	useKeyboardShortcut("ArrowLeft", prev, !isOpen, 2);
	useKeyboardShortcut("ArrowRight", next, !isOpen, 2);

	useEffect(() => {
		if (!data) {
			return;
		}

		manager.download(data.unified_uri, {
			onSuccess: (event) => {
				setFile(event.value);
			},
		});
	}, [current, data]);

	useEffect(() => {
		const div = document.createElement("div");
		div.id = "navigation-portal";
		document.body.appendChild(div);
		portalContainerRef.current = div;

		return () => {
			document.body.removeChild(div);
		};
	}, []);

	const handleDownload = (): void => {
		if (!file || !file?.source) return;

		try {
			downloadFile({
				downloadUrl: file.source,
				fileName: formatFileName(file.name, file.ext || undefined),
			});
		} catch (error) {
			// TODO: handle error
		}
	};

	const handleNextAttachment = (
		event: React.MouseEvent<HTMLButtonElement>,
	): void => {
		blockEvent(event);
		next();
	};

	const handlePreviousAttachment = (
		event: React.MouseEvent<HTMLButtonElement>,
	): void => {
		blockEvent(event);
		prev();
	};

	const handleZoomIn = (): void => {
		setZoom((prev) => Math.min(prev + 0.2, 2.0));
	};

	const handleZoomOut = (): void => {
		setZoom((prev) => Math.max(prev - 0.2, 0.4));
	};

	const handleResetZoom = (): void => {
		setZoom(0.8);
	};

	const zoomProps = {
		zoom,
		onZoomIn: handleZoomIn,
		onZoomOut: handleZoomOut,
		onResetZoom: handleResetZoom,
	};

	const renderPreview = (): React.ReactElement | null => {
		if (!file?.source) return null;

		switch (file.ext?.toLowerCase()) {
			case "jpg":
			case "jpeg":
			case "png":
			case "gif":
			case "webp":
				return (
					<ImageRenderer url={file.source} zoom={zoom}>
						<ZoomControls {...zoomProps} />
					</ImageRenderer>
				);
			case "pdf":
				return (
					<PdfRenderer url={file.source} zoom={zoom}>
						{(props) => (
							<ZoomControls {...zoomProps} showPageNumber {...props} />
						)}
					</PdfRenderer>
				);
			case "xlsx":
			case "xls":
			case "csv":
				return (
					<SpreadsheetRenderer
						fileType={file.ext}
						url={file.source}
						zoom={zoom}
					/>
				);
			case "docx":
				return (
					<DocxRenderer url={file.source} zoom={zoom}>
						<ZoomControls {...zoomProps} />
					</DocxRenderer>
				);
			case "txt":
			default:
				return (
					<TextRenderer url={file.source} zoom={zoom}>
						<ZoomControls {...zoomProps} />
					</TextRenderer>
				);
		}
	};

	if (!file) {
		return <div>loading...</div>;
	}

	const formattedFileName = formatFileName(
		file.name || "",
		file.ext || undefined,
	);

	return (
		<>
			<Modal onOpenChange={close} open={isOpen}>
				<Content
					className="h-auto max-h-[95vh] w-full max-w-[90vw] overflow-hidden p-0 z-[9999] rounded-modal"
					closeButton={false}
					title={formattedFileName}
					description={`Preview of ${formattedFileName}`}
				>
					<VStack className="relative h-full overflow-hidden rounded-modal">
						<div
							className="flex w-full items-center justify-between rounded-t-modal bg-surface-inverted px-6 py-3"
							role="dialog"
							aria-labelledby="preview-title"
						>
							<div className="flex-1">
								<span
									id="preview-title"
									className="font-semibold text-text-body-muted"
								>
									{formattedFileName}
								</span>
							</div>
							<ButtonGroup className="gap-sm">
								<Button
									className="text-text-icon outline-none hover:text-text-icon/80"
									onClick={handleDownload}
								>
									<Icon kind="download" /> Download
								</Button>
								<Button
									className="text-text-icon outline-none hover:text-text-icon/80"
									onClick={close}
									size="icon"
								>
									<Icon kind="close" />
								</Button>
							</ButtonGroup>
						</div>
						<div>
							<div
								className="h-full flex items-center absolute left-0 top-0 bottom-0 z-10 p-lg"
								onClick={prev}
							>
								<Button
									size="icon"
									disabled={current <= 0}
									className={current <= 0 || total <= 1 ? "hidden" : ""}
								>
									<Icon kind="arrow-left" />
								</Button>
							</div>
							{renderPreview()}
							<div
								className="h-full flex items-center absolute right-0 top-0 bottom-0 z-10 p-lg"
								onClick={next}
							>
								<Button
									size="icon"
									disabled={current > total - 1}
									className={current > total - 1 || total <= 1 ? "hidden" : ""}
								>
									<Icon kind="arrow-right" />
								</Button>
							</div>
						</div>
					</VStack>
				</Content>
			</Modal>
			{isOpen && portalContainerRef.current
				? createPortal(
						<NavigationButtons
							attachmentsLength={total}
							currentIndex={current}
							onNext={handleNextAttachment}
							onPrevious={handlePreviousAttachment}
						/>,
						portalContainerRef.current,
					)
				: null}
		</>
	);
}
